import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, unref as _unref, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "flex items-center gap-2 px-3 py-2 border border-primary30 rounded bg-primary5 mb-4",
  "data-testid": "info-operation-role"
}
const _hoisted_2 = { class: "text-sm text-secondary50" }
const _hoisted_3 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}

import { ref, computed, inject } from 'vue'

import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiFormTextboxLock } from '@mdi/js'

import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { groupToApiLayers } from '@/utils/approval-flow-transformers'
import { useImportApprovalFlow } from '@/hooks/useImportApprovalFlow'
import ApprovalLayerCard from './components/ApprovalLayerCard.vue'
import ApprovalFlowPreview from './ApprovalFlowPreview.vue'
import ScheduleStepHeading from './ScheduleStepHeading.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ImportExceptionalityConfigModal from '@/components/designSystem/modals/ImportExceptionalityConfigModal/ImportExceptionalityConfigModal.vue'
import SaveApprovalTemplateModal from '@/components/designSystem/modals/SaveApprovalTemplateModal/SaveApprovalTemplateModal.vue'

import {
  LayerOperator,
  MANDATORY_VALIDATION_ROLES,
  VALIDATION_ROLE_LABELS
} from '@/types/approval-flow.types'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleApprovalFlow',
  props: {
    errors: {},
    onClearError: { type: Function },
    showErrors: { type: Boolean }
  },
  setup(__props: any) {



const toast = useToast()
const { notifyError } = useErrorToast()
const { importConfig } = useImportApprovalFlow()
const store = (inject('createScheduleStore') ?? useCreateScheduleStore()) as ReturnType<
  typeof useCreateScheduleStore
>
const { stepApprovalFlow } = storeToRefs(store)
const approvalFlowStore = useApprovalFlowStore()
const companyStore = useCompanyStore()

const currentCompanyId = computed(() => {
  return companyStore.myCompanies[0]?.id
})

// Deriva do conjunto real de obrigatórios (varia com `enabledMandatoryRiskValidator`)
// em vez de citar os papéis em texto fixo, que mentiria com a flag desligada.
const mandatoryRolesLabel = computed(() =>
  MANDATORY_VALIDATION_ROLES.map((role) => VALIDATION_ROLE_LABELS[role]).join(' e ')
)

stepApprovalFlow.value.enabled = true

const importModalRef = ref<InstanceType<typeof ImportExceptionalityConfigModal> | null>(null)
const isImportModalOpen = ref(false)
const isSaveTemplateModalOpen = ref(false)
const isSavingTemplate = ref(false)
const savedTemplate = ref<{ id: number; name: string } | null>(null)

const handleImportConfig = async (payload: { source: 'template' | 'scale'; id: number }) => {
  const imported = await importConfig(payload)
  if (imported) {
    stepApprovalFlow.value.layers[0] = imported
    isImportModalOpen.value = false
    importModalRef.value?.onImportSuccess()
  } else {
    isImportModalOpen.value = false
  }
  savedTemplate.value = null
}

const handleImportClose = () => {
  isImportModalOpen.value = false
  savedTemplate.value = null
}

const handleSaveTemplate = async (payload: { name: string; companyId: number }) => {
  isSavingTemplate.value = true
  try {
    const layers = groupToApiLayers(stepApprovalFlow.value.layers[0])
    const created = await approvalFlowStore.createTemplate({
      name: payload.name,
      companyId: payload.companyId,
      layers
    })
    savedTemplate.value = { id: created.id, name: created.name }
    isSaveTemplateModalOpen.value = false
    isImportModalOpen.value = true
    toast.success('Modelo salvo com sucesso.')
  } catch (e) {
    notifyError(e, 'Não foi possível salvar o modelo de configuração.')
  } finally {
    isSavingTemplate.value = false
  }
}

function updateLayer(updated: ApprovalGroup) {
  stepApprovalFlow.value.layers[0] = updated
}

const previewLayers = computed<ApprovalLayerDetail[]>(() => {
  const apiLayers = groupToApiLayers(stepApprovalFlow.value.layers[0])
  return apiLayers.map((layer, idx) => ({
    id: idx,
    order: layer.order,
    operator: layer.operator ?? LayerOperator.AND,
    roles: (layer.roles ?? []).map((r, rIdx) => ({
      id: rIdx,
      role: r.role,
      weight: r.weight ?? rIdx + 1
    }))
  }))
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(ScheduleStepHeading, {
      title: "Configurações de excepcionalidade",
      class: "mb-6"
    }, {
      default: _withCtx(() => [
        _createVNode(OutlineButton, {
          text: "Salvar como modelo",
          width: "auto",
          height: "36px",
          "data-testid": "save-template-button",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (isSaveTemplateModalOpen.value = true))
        }),
        _createVNode(OutlineButton, {
          text: "Importar configurações",
          width: "auto",
          height: "36px",
          "data-testid": "import-exceptionality-button",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (isImportModalOpen.value = true))
        })
      ]),
      _: 1
    }),
    _createVNode(ImportExceptionalityConfigModal, {
      ref_key: "importModalRef",
      ref: importModalRef,
      "modal-active": isImportModalOpen.value,
      "saved-template": savedTemplate.value,
      "company-id": currentCompanyId.value,
      onClose: handleImportClose,
      onSave: handleImportConfig
    }, null, 8, ["modal-active", "saved-template", "company-id"]),
    _createVNode(SaveApprovalTemplateModal, {
      "modal-active": isSaveTemplateModalOpen.value,
      layers: previewLayers.value,
      loading: isSavingTemplate.value,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (isSaveTemplateModalOpen.value = false)),
      onSave: handleSaveTemplate
    }, null, 8, ["modal-active", "layers", "loading"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_v_icon, {
        icon: _unref(mdiFormTextboxLock),
        size: "18",
        class: "text-primary30 shrink-0"
      }, null, 8, ["icon"]),
      _createElementVNode("span", _hoisted_2, " Os papéis validadores " + _toDisplayString(mandatoryRolesLabel.value) + " são obrigatórios no fluxo de validação de excepcionalidade, e não podem ser deletados ", 1)
    ]),
    _createVNode(ApprovalLayerCard, {
      group: _unref(stepApprovalFlow).layers[0],
      "show-errors": _ctx.showErrors,
      "data-testid": "approval-layer-0",
      "onUpdate:group": _cache[3] || (_cache[3] = (updated) => updateLayer(updated))
    }, null, 8, ["group", "show-errors"]),
    (_ctx.errors.layers)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.errors.layers), 1))
      : _createCommentVNode("", true),
    (previewLayers.value.length > 0)
      ? (_openBlock(), _createBlock(ApprovalFlowPreview, {
          key: 1,
          layers: previewLayers.value,
          class: "mt-4"
        }, null, 8, ["layers"]))
      : _createCommentVNode("", true)
  ]))
}
}

})